import { OverlayRectangle } from '@policyvision/contracts';
import { ArrowDownToLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DocumentViewerOverlayContext } from '@/attachment/component/DocumentViewer';
import {
  RectangleOverlay,
  usePdfOverlay,
} from '@/attachment/hooks/usePdfOverlay';
import { usePdfScroll } from '@/attachment/hooks/usePdfScroll';
import { useThemeColors } from '@/common/hooks/useThemeColors';
import { useValidation } from '@/policy/contexts';

export interface SectionBoundingBox extends OverlayRectangle {
  sectionUuid: string;
}

interface PolicySectionOverlayProps extends DocumentViewerOverlayContext {
  boundingBoxes: SectionBoundingBox[];
  onRectangleClick: (sectionUuid: string) => void;
  selectedSectionUuid: string | null;
}

// Draws the sections' bounding boxes on the rendered pdf pages, coloured by validation status
export const PolicySectionOverlay = ({
  pageElements,
  scale,
  scrollToPage,
  boundingBoxes,
  onRectangleClick,
  selectedSectionUuid,
}: PolicySectionOverlayProps) => {
  const { t } = useTranslation();
  const { findValidation } = useValidation();
  const themeColors = useThemeColors();

  const getColorsByValidation = (identifier: string) => {
    const validation = findValidation(identifier);

    if (validation.status === 'COHERENT' || validation.status === 'ERROR') {
      return {
        color: themeColors.boundingBox.muted,
        borderColor: themeColors.boundingBox.muted,
      };
    }

    return {
      color: themeColors.boundingBox.primary,
      borderColor: themeColors.boundingBox.borderPrimary,
    };
  };

  const rectangleOverlays: RectangleOverlay[] = boundingBoxes.map((box) => ({
    ...box,
    ...getColorsByValidation(box.sectionUuid),
  }));

  usePdfOverlay({
    pageElements,
    boundingBoxes: rectangleOverlays,
    onRectangleClick,
    selectedSectionUuid,
    scale,
    scrollToPage,
    continuedOnLabel: t('navigation.continuedOnPage'),
    ctaIcon: ArrowDownToLine,
  });

  usePdfScroll(pageElements, boundingBoxes, selectedSectionUuid);

  return null;
};
